<template>
<div class="container mx-auto h-auto mt-40 flex-col flex md:justify-center min-h-0">
  <div class="z-10 md:p-5 laptop:p-0 px-5">
    <MARSHeading>
      {{ $t('support.heading') }}
    </MARSHeading>
  </div>
  <p class="laptop:w-1/2 mt-4" v-html="$t('support.description')"></p>
</div>

<Block2Cols>
  <template #col1>
    <MARSTitle>{{ $t('support.easyToUseTitle') }}</MARSTitle>
    <p v-html="$t('support.easyToUseDescription')"></p>
  </template>
  <template #col2>
    <p v-html="$t('support.familiarProcess')"></p>
    <p v-html="$t('support.documentationAndVideos')"></p>
  </template>
</Block2Cols>

<Block2Cols compact class="mt-8">
  <template #col1>
    <picture>
      <source type="image/webp" srcset="~/assets/img/landing/DocumentationWebsite.webp">
      <source type="image/jpeg" srcset="~/assets/img/landing/DocumentationWebsite.jpg">
      <img loading="lazy" src="~/assets/img/landing/DocumentationWebsite.jpg" :alt="$t('support.image_alt.documentationWebsite')">
    </picture>
  </template>
  <template #col2>
    <picture>
      <source type="image/webp" srcset="~/assets/img/landing/VideoTutorialsSeriesBasic.webp">
      <source type="image/jpeg" srcset="~/assets/img/landing/VideoTutorialsSeriesBasic.jpg">
      <img loading="lazy" src="~/assets/img/landing/VideoTutorialsSeriesBasic.jpg" :alt="$t('support.image_alt.videoTutorialSeriesBasic')">
    </picture>
    <p>...and more</p>
  </template>
</Block2Cols>

<Block2Cols>
  <template #col1>
    <MARSTitle>{{ $t('support.softwareTutorialsTitle') }}</MARSTitle>
  </template>
  <template #col2>
    <p v-html="$t('support.softwareTutorialsDescription')"></p>
  </template>
</Block2Cols>

<Block2Cols compact class="mt-8">
  <template #col1>
    <div class="flex flex-col-reverse sm:flex-row gap-4 items-center">
      <div>
        <img loading="lazy" class="sw-img" src="~/assets/img/software/Keyshot logo.webp" :alt="$t('support.image_alt.keyshotLogoAlt')">
      </div>
      <div class="sw-logo">Keyshot</div>
    </div>
  </template>
  <template #col2>
    <div class="flex flex-col-reverse sm:flex-row gap-4 items-center">
      <div>
        <img loading="lazy" class="sw-img" src="~/assets/img/software/Blender logo.webp" :alt="$t('support.image_alt.blenderLogoAlt')">
      </div>
      <div class="sw-logo">Blender</div>
    </div>
  </template>
</Block2Cols>

<Block2Cols compact class="mt-16">
  <template #col1>
    <div class="flex flex-col-reverse sm:flex-row gap-4 items-center">
      <div>
        <img loading="lazy" class="sw-img" src="~/assets/img/software/Substance 3D Painter logo.webp" :alt="$t('support.image_alt.substanceLogoAlt')">
      </div>
      <div class="sw-logo leading-none">Substance 3D Painter</div>
    </div>
  </template>
  <template #col2>
    <div class="flex flex-col-reverse sm:flex-row gap-4 items-center">
      <div>
        <img loading="lazy" class="sw-img" src="~/assets/img/software/Cinema 4D logo.webp" :alt="$t('support.image_alt.cinema4DLogoAlt')">
      </div>
      <div class="sw-logo">Cinema 4D</div>
    </div>
  </template>
</Block2Cols>

<Block2Cols compact class="mt-16">
  <template #col1>
    <div class="flex flex-col-reverse sm:flex-row gap-4 items-center">
      <div>
        <img loading="lazy" class="sw-img" src="~/assets/img/software/3DS Max logo.webp" :alt="$t('support.image_alt.studioMaxLogoAlt')">
      </div>
      <div>
        <div class="sw-logo leading-none">3D Studio Max</div>
        <p v-html="$t('support.studioMaxComingSoon')"></p>
      </div>
    </div>
  </template>
  <template #col2>
    <div class="flex flex-col-reverse sm:flex-row gap-4 items-center">
      <div>
        <img loading="lazy" class="sw-img" src="~/assets/img/software/more.png" :alt="$t('support.image_alt.plusIcon')">
      </div>
      <div class="font-bold text-35xl">{{ $t('support.moreToCome') }}</div>
    </div>
  </template>
</Block2Cols>

<div ref="videoContainer" class="relative w-full overflow-hidden pt-10 laptop:mt-100px mx-auto" style="max-width:2560px;">
  <video autoplay loop muted playsinline class="w-full h-auto"
    :src="isVideoLoaded ? videoSource : ''"
    :poster="videoPoster"
  >
    Your browser does not support the video tag.
  </video>
</div>

<Block2Cols class="italic">
  <template #col1>
    <MARSTitle>{{ $t('support.caseStudyTitle') }}</MARSTitle>
    <p v-html="$t('support.caseStudyDescription1')"></p>
  </template>
  <template #col2>
    <p v-html="$t('support.caseStudyDescription2')"></p>
    <p v-html="$t('support.optimizationImportance')"></p>
  </template>
</Block2Cols>

<Block2Cols compact items-center class="mt-16 italic">
  <template #col1>
    <p v-html="$t('support.politecnicoClass')"></p>
  </template>
  <template #col2>
    <div class="flex justify-center">
      <picture>
        <source type="image/webp" srcset="~/assets/img/landing/Politecnico-logo.webp">
        <source type="image/png" srcset="~/assets/img/landing/Politecnico-logo.png">
        <img loading="lazy" style="height: 150px;" src="~/assets/img/landing/Politecnico-logo.png" :alt="$t('support.image_alt.politecnicoLogoAlt')">
      </picture>
    </div>
  </template>
</Block2Cols>

<Block2Cols compact class="mt-16 italic">
  <template #col1>
    <p v-html="$t('support.noNeedToBecomeStudent')"></p>
  </template>
  <template #col2>
    <div>
      <picture>
        <source type="image/webp" srcset="~/assets/img/landing/VideoTutorialsSeriesKeyshot.webp">
        <source type="image/jpeg" srcset="~/assets/img/landing/VideoTutorialsSeriesKeyshot.jpg">
        <img loading="lazy" src="~/assets/img/landing/VideoTutorialsSeriesKeyshot.jpg" :alt="$t('support.image_alt.keyshotTutorialSeriesAlt')">
      </picture>
    </div>
  </template>
</Block2Cols>

<Block2Cols itemsEnd>
  <template #col1>
    <MARSTitle>{{ $t('support.professionalSupportTitle') }}</MARSTitle>
    <p v-html="$t('support.professionalSupportDescription1')"></p>
  </template>
  <template #col2>
    <p v-html="$t('support.professionalSupportDescription2')"></p>
  </template>
</Block2Cols>

<div class="container mx-auto">
  <div class="mx-5 md:mx-8 laptop:mx-24">
    <div class="grid grid-cols-1 laptop:grid-cols-4 md:grid-cols-2 gap-x-8 mb-12">
      <div class="w-full px-0 laptop:pl-0 my-6 laptop:my-12">
        <img loading="lazy" src="~/assets/img/ico-training.svg" :alt="$t('support.image_alt.trainingIconAlt')">
        <p class="font-bold my-4 laptop:my-8">{{ $t('support.trainingTitle') }}</p>
        <p v-html="$t('support.trainingDescription')"></p>
      </div>
      <div class="w-full px-0 my-6 laptop:my-12">
        <img loading="lazy" src="~/assets/img/ico-save.svg" :alt="$t('support.image_alt.saveIconAlt')">
        <p class="font-bold my-4 laptop:my-8">{{ $t('support.workflowOptimizationTitle') }}</p>
        <p v-html="$t('support.workflowOptimizationDescription')"></p>
      </div>
      <div class="w-full px-0 my-6 laptop:my-12">
        <img loading="lazy" src="~/assets/img/ico-model-optimization.svg" :alt="$t('support.image_alt.modelOptimizationIconAlt')">
        <p class="font-bold my-4 laptop:my-8">{{ $t('support.qualityImprovementTitle') }}</p>
        <p v-html="$t('support.qualityImprovementDescription')"></p>
      </div>
      <div class="w-full px-0 my-6 laptop:my-12">
        <img loading="lazy" src="~/assets/img/ico-map.svg" :alt="$t('support.image_alt.mapIconAlt')">
        <p class="font-bold my-4 laptop:my-8">{{ $t('support.decisionSupportTitle') }}</p>
        <p v-html="$t('support.decisionSupportDescription')"></p>
      </div>
    </div>
  </div>
</div>

<Block2Cols>
  <template #col1>
    <MARSTitle>{{ $t('support.accountManagerTitle') }}</MARSTitle>
    <p v-html="$t('support.accountManagerDescription1')"></p>
  </template>
  <template #col2>
    <p v-html="$t('support.accountManagerDescription2')"></p>
    <p v-html="$t('support.accountManagerDescription3')"></p>
  </template>
</Block2Cols>

<Block2Cols>
  <template #col1>
    <MARSTitle><div v-html="$t('services.model')" /></MARSTitle>

    <p class="text-standard laptop:text-slg md:text-base leading-7" v-html="$t('services.model-text-1')" />
    <p class="text-standard laptop:text-slg md:text-base leading-7" v-html="$t('services.model-text-2')" />
    <p class="text-standard laptop:text-slg md:text-base leading-7" v-html="$t('services.model-text-3')" />

    <ActionButton bg="highlight" route="contacts" margin-top="mt-5" margin-top-fullhd="fullhd:mt-10"
                  margin-top-laptop="laptop:mt-5" margin-top-tablet="md:mt-5" :value="$t('services.cta')" />
  </template>

  <template #col2>
    <div class="grid grid-cols-1 gap-x-8 gap-y-16 h-full md:text-slg">
      <div class="flex items-center">
        <img loading="lazy" src="~/assets/img/ico-lens.svg" :alt="$t('support.image_alt.lensIconAlt')">
        <b class="ml-8" v-html="$t('services.model-text-4')" />
      </div>
      <div class="flex items-center">
        <img loading="lazy" src="~/assets/img/ico-export.svg" :alt="$t('support.image_alt.exportIconAlt')">
        <b class="ml-8" v-html="$t('services.model-text-5')" />
      </div>
      <div class="flex items-center">
        <img loading="lazy" src="~/assets/img/ico-materials.svg" :alt="$t('support.image_alt.materialsIconAlt')">
        <b class="ml-8" v-html="$t('services.model-text-6')" />
      </div>
      <div class="flex items-center">
        <img loading="lazy" src="~/assets/img/ico-distribution.svg" :alt="$t('support.image_alt.distributionIconAlt')">
        <b class="ml-8" v-html="$t('services.model-text-7')" />
      </div>
    </div>
  </template>
</Block2Cols>

<div class="container mx-auto flex flex-col laptop:flex-row mt-12">
  <div class="w-11/12 mx-auto lg:px-8">
    <div class="w-full grid grid-cols-1 lg:grid-cols-3 gap-3">
      <img loading="lazy" src="~/assets/img/services-3d-01.webp" :alt="$t('support.image_alt.services3dImage1Alt')">
      <img loading="lazy" src="~/assets/img/services-3d-02.webp" :alt="$t('support.image_alt.services3dImage2Alt')">
      <img loading="lazy" src="~/assets/img/services-3d-03.webp" :alt="$t('support.image_alt.services3dImage3Alt')">
    </div>
  </div>
</div>

<Block1Col>
  <MARSTitle>{{ $t('support.customerSuccessTitle') }}</MARSTitle>
  <p class="mt-2" v-html="$t('support.customerSuccessDescription1')"></p>
  <NuxtLink external :to="$t('support.customerSuccessLink').toString()"
    :title="$t('support.customerSuccessDescription2').toString()"
  >
    <p v-html="$t('support.customerSuccessDescription2')" class="underline hover:text-highlight"></p>
  </NuxtLink>
  <div class="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-24 gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
    <article v-for="post in posts" :key="post.id" class="flex flex-col items-start justify-between">
      <a :href="post.href" target="_blank" class="hover:text-highlight group">
        <div class="relative w-full">
          <img :src="post.imageUrl" alt="" class="aspect-[16/9] w-full object-fit sm:aspect-[2/1] lg:aspect-[3/2]" />
          <div class="absolute" />
        </div>
        <div class="max-w-xl">
          <div class="relative">
            <h3 class="mt-3 text-lg font-semibold leading-6 ">
                <span class="absolute inset-0" />
                {{ post.title }}
            </h3>
            <p class="mt-5 line-clamp-3 text-sm leading-6 group-hover:text-highlight">{{ post.description }}</p>
          </div>
        </div>
      </a>
    </article>
  </div>
</Block1Col>

</template>

<script setup lang="ts">
import { useIubenda } from 'mars-iubenda-vite';

import logoSalewa from '~/assets/img/customer-success-stories/Salewa Logo List.png'
import logoExor from '~/assets/img/customer-success-stories/EXOR International logo list.png'
import logoAtlasCopco from '~/assets/img/customer-success-stories/AtlasCopco-logo-list.png'

const {youtubeEnabled} = useIubenda()

// Define the video sources
import largeVideo from '~/assets/video/landing/lots_of_documentation_desktop.webm'
import smallVideo from '~/assets/video/landing/lots_of_documentation_mobile.webm'
import largeVideoPoster from '~/assets/video/landing/lots_of_documentation_desktop.webp'
import smallVideoPoster from '~/assets/video/landing/lots_of_documentation_mobile.webp'

// Reactive variable to hold the correct video source
const videoSource = ref('')
const videoPoster = ref('')
const isVideoLoaded = ref(false)
const videoContainer = ref(null)

// Function to set the appropriate video source based on screen size
const setVideoSource = () => {
  if (window.innerWidth >= 1280) {
    videoSource.value = largeVideo // Use larger video for larger screens
    videoPoster.value = largeVideoPoster
  } else {
    videoSource.value = smallVideo // Use smaller video for smaller screens
    videoPoster.value = smallVideoPoster
  }
}


const posts = [
  {
    id: 1,
    title: 'Salewa',
    href: '/explore/salewa-revolutionizes-outdoor-gear-presentation-with-my-ar-studio/',
    description:
      'The marketing team at Salewa, tasked with finding innovative ways to present their large-scale technical tents, sought a solution that could enhance customer interaction and understanding of their complex products.',
    imageUrl: logoSalewa,
    date: 'Mar 16, 2020',
    datetime: '2020-03-16',
    category: { title: 'Marketing', href: '#' },
    author: {
      name: 'Michael Foster',
      role: 'Co-Founder / CTO',
      href: '#',
      imageUrl:
        'https://images.unsplash.com/photo-1519244703995-f4e0f30006d5?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    },
  },
  {
    id: 2,
    title: 'EXOR International',
    href: '/explore/exor-international/',
    description:
      'Exor International, a global leader in industrial automation solutions, has embarked on a journey to innovate and modernize how they present and distribute technical and marketing information. In their quest for advanced tools, Exor found My AR Studio to be the ideal partner.',
    imageUrl: logoExor,
    date: 'Mar 16, 2020',
    datetime: '2020-03-16',
    category: { title: 'Marketing', href: '#' },
    author: {
      name: 'Michael Foster',
      role: 'Co-Founder / CTO',
      href: '#',
      imageUrl:
        'https://images.unsplash.com/photo-1519244703995-f4e0f30006d5?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    },
  },
  {
    id: 3,
    title: 'Atlas Copco IAS UK',
    href: '/explore/atlas-copco-ias-uk/',
    description:
      'The Digital Publications team of Atlas Copco IAS UK is tasked with modernizing their manuals and information delivery. The team explored innovative solutions like augmented reality, discovering that My AR Studio could be a valuable asset.',
    imageUrl: logoAtlasCopco,
    date: 'Mar 16, 2020',
    datetime: '2020-03-16',
    category: { title: 'Marketing', href: '#' },
    author: {
      name: 'Michael Foster',
      role: 'Co-Founder / CTO',
      href: '#',
      imageUrl:
        'https://images.unsplash.com/photo-1519244703995-f4e0f30006d5?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    },
  },
]

let observer: IntersectionObserver | null = null
const handleLazyLoad = (entries: IntersectionObserverEntry[]) => {
  const [entry] = entries  // Get the first entry

  if (entry.isIntersecting) {
    // If the video is in the viewport, set the video source and allow loading
    setVideoSource()
    isVideoLoaded.value = true
    observer?.disconnect()  // Stop observing once the video has been loaded
  }
}
// Listen for screen size changes and set video source accordingly
onMounted(() => {
  setVideoSource()
  window.addEventListener('resize', setVideoSource)

  observer = new IntersectionObserver(handleLazyLoad, {
    root: null,
    threshold: 0,
    rootMargin: '550px',
  })
  if (videoContainer.value) {
    observer.observe(videoContainer.value)  // Start observing the video container
  }
})
onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect()
  }
})
</script>

<style scoped>
.sw-logo {
  @apply laptop:text-45xl lg:text-35xl md:text-35xl font-bold;
}
.sw-img {
  width: 120px;
  min-width: 120px;
}
</style>
